import { useQuery } from '@tanstack/react-query'
import { Link, useRouterState } from '@tanstack/react-router'
import {
  AppShell,
  AppShellBack,
  AppShellBrand,
  Button,
  Skeleton,
  useCollapsedRail,
  VoleeMark,
  VoleeLockup,
} from '@volee/ui'
import { lazy, Suspense, useCallback, useState, type ReactNode } from 'react'

import { meQueryOptions } from '../auth/me.ts'
import { messages } from '../messages/index.ts'
import { barNavigation } from '../navigation/bar.ts'
import { ArrowStepContext, useHeaderBack, type SetArrowStep } from '../navigation/header-back.ts'
import { BarSections, NavSections } from '../navigation/nav-sections.tsx'
import { useIsFlow } from '../navigation/use-is-flow.ts'
import { useNavigationEntries } from '../navigation/use-navigation-entries.ts'
import { useStatusBarTone } from '../navigation/use-status-bar-tone.ts'
import { NAV_PATHS } from '../navigation/paths.ts'

// The shell comes from @volee/ui and the user from `useQuery(['me'])`, not a store (§4.4).
// Why each choice: apps/frontend/docs/razionale-che-il-codice-indica.md.

// Split out, as the back office splits its own: static, it puts a measured 5.3 kB gzip of
// Radix menu in the entry chunk, which the anonymous funnel downloads and never renders.
const UserMenu = lazy(() => import('./user-menu.tsx').then((m) => ({ default: m.UserMenu })))

/** The footprint of the sign-in button, so resolving the session shifts no layout. */
function AuthSlotSkeleton() {
  return <Skeleton className="h-(--size-control-sm) w-24" />
}

export function Layout({ children }: { children: ReactNode }) {
  // A 401 here is a legitimate answer, so no `enabled: false`:
  // apps/frontend/docs/razionale-che-il-codice-indica.md.
  const { data: me, isPending: sessionPending } = useQuery(meQueryOptions)
  const entries = useNavigationEntries()
  const theater = useRouterState({
    select: (status) => status.matches.at(-1)?.staticData.theater === true,
  })
  const { collapsed, setCollapsed } = useCollapsedRail(theater)
  const headerBack = useHeaderBack()
  // What a page that is in a mode of its own has the arrow do: the box lives here, in the one place
  // that draws the arrow. A function in state, so it is stored through an updater.
  const [arrowStep, setArrowStepState] = useState<(() => void) | null>(null)
  const setArrowStep = useCallback<SetArrowStep>((step) => setArrowStepState(() => step), [])
  // Declared by the route, not inferred from a list of paths here. `entry` is declared once, on
  // the entrance's layout route, and every route under it is inside it: any match, not the leaf.
  const chrome = useRouterState({
    select: (status) =>
      status.matches.some((match) => match.staticData.chrome === 'entry')
        ? 'entry'
        : status.matches.at(-1)?.staticData.chrome,
  })
  const opensOnHero = useRouterState({
    select: (status) => status.matches.at(-1)?.staticData.opensOnHero === true,
  })
  const bare = chrome === 'bare'
  // A flow keeps the app's header, mark and account, and hides only the navigation: it has its own
  // way on and out in a bar at the foot.
  const flow = useIsFlow()
  // The shell is the one place every route passes through, so the one place that knows which
  // ground the screen is on: the entrance's, or the app's.
  useStatusBarTone(chrome === 'entry' ? 'entrance' : 'app')

  // A bare page owns its own way in and out, so the shell offers neither. And a session in
  // flight is not an absent one: collapsing the two flashed "Accedi" at a signed-in user.
  function headerAuth() {
    if (bare) return null
    if (sessionPending) return <AuthSlotSkeleton />
    if (me === undefined) {
      return (
        <Button asChild variant="outline" size="sm">
          {/* No `search`: writing the default back stamped `?returnTo=%2F` on every landing. */}
          <Link to={NAV_PATHS.signIn}>{messages.navigation.signIn}</Link>
        </Button>
      )
    }
    return (
      <Suspense fallback={<AuthSlotSkeleton />}>
        <UserMenu me={me} />
      </Suspense>
    )
  }

  function brand() {
    // Every page's header on a phone is the account alone: the bar's home is the way home, and a
    // flow, with no bar, does not grow a mark the rest of the app never shows.
    if (flow) return undefined
    const mark = <VoleeMark label={messages.app.name} />
    if (bare)
      return (
        <AppShellBrand variant="plain">
          <VoleeLockup label={messages.app.name} />
        </AppShellBrand>
      )
    return (
      <AppShellBrand asChild>
        <Link to={NAV_PATHS.home}>{mark}</Link>
      </AppShellBrand>
    )
  }

  // `null` is a state and not an empty value — no column, no bar: a bare page has its own
  // way out, and a session with no facet has nowhere to navigate.
  function navigation() {
    if (bare || flow || entries.length === 0) return null
    return <NavSections entries={entries} />
  }

  function bottomBar() {
    const bar = barNavigation(entries)
    if (bare || flow || bar === null) return undefined
    return <BarSections bar={bar} />
  }

  function backArrow() {
    if (bare || flow || !headerBack.shown) return undefined
    return (
      <AppShellBack label={messages.navigation.back} onClick={arrowStep ?? headerBack.goBack} />
    )
  }

  // The entrance's frame is the whole page, safe area included: the shell draws nothing around it.
  if (chrome === 'entry') return children

  return (
    <AppShell
      navigationLabel={messages.app.navigationLabel}
      openMenuLabel={messages.app.openMenu}
      closeMenuLabel={messages.app.closeMenu}
      collapse={{
        collapsed,
        onCollapsedChange: setCollapsed,
        collapseLabel: messages.app.collapseMenu,
        expandLabel: messages.app.expandMenu,
      }}
      brand={brand()}
      account={headerAuth()}
      sidebar={navigation()}
      bottomBar={bottomBar()}
      back={backArrow()}
      opensOnHero={opensOnHero}
    >
      <ArrowStepContext.Provider value={setArrowStep}>{children}</ArrowStepContext.Provider>
    </AppShell>
  )
}
